import { useEffect, useRef, useState } from "react";
import {
  type PairOccurrence,
  type PairOccurrenceFilter,
  STUDENT_LESSON_PAGE_LIMIT_DEFAULT,
  STUDENT_LESSON_PAGE_LIMIT_MAX,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { listErrorText } from "../../lib/list-error.ts";

/**
 * Занятия пары на карточке ученика: первая страница из карточки плюс
 * догруженные по кнопке (образец `homework/hub/use-homework-list.ts`, но свой:
 * общего хука на два списка не выделяем).
 *
 * ⚠️ Первая страница приезжает В КАРТОЧКЕ (`GET /v1/students/:id`), а не
 * отдельным запросом: карточку открывают переходом, и лишний круг по сети на
 * канале 128 кбит/с человек видит как подвисание. Поэтому хук не грузит первую
 * страницу сам, а принимает её снаружи. Исключение одно: первая страница под
 * фильтром «Неоплаченные», её в карточке нет, и она грузится ручкой.
 *
 * ⚠️ **Перечитывание карточки не сбрасывает глубину списка.** Карточка
 * перечитывается после любого действия на экране (отметка, черта, анкета,
 * ссылка), и список, долистанный до сорока строк, прыгал бы обратно к десяти.
 * Поэтому, если загружено было больше первой страницы (или включён фильтр),
 * хук сам перечитывает первые N строк одним запросом (`limit = min(загружено,
 * MAX)`), а до ответа держит на экране прежние строки: мигания до десяти нет,
 * а отмеченная строка обновляется ответом. Склеивать новую первую страницу со
 * старыми догруженными нельзя, курсоры у них от разных чтений.
 *
 * ⚠️ **Фильтр это параметр ручки, а не отбор здесь**: браузер видит только
 * загруженные страницы, и отбор поверх них врал бы при пагинации. Смена
 * фильтра грузит список заново с первой страницы.
 *
 * Долистывание по кнопке, а не по прокрутке: разбор у `useHomeworkList`.
 */
export interface StudentOccurrencesView {
  rows: readonly PairOccurrence[];
  /** `null` значит «дальше ничего нет»: кнопки «Показать ещё» тогда нет. */
  nextCursor: string | null;
  busy: boolean;
  err: string | null;
  loadMore: () => void;
  filter: PairOccurrenceFilter | null;
  setFilter: (filter: PairOccurrenceFilter | null) => void;
}

/** Своё состояние списка поверх первой страницы карточки. */
interface Own {
  rows: PairOccurrence[];
  cursor: string | null;
}

export function useStudentOccurrences(
  studentId: string,
  first: { rows: readonly PairOccurrence[]; nextCursor: string | null },
  /**
   * Задают ли у этой пары вопрос «не оплачено»: учёт оплат включён, и пара
   * платит поурочно. ⚠️ Без него фильтр не уходит в запрос НИКОГДА, а уже
   * включённый сбрасывается: анкету правят на том же экране, и после смены
   * режима на помесячный чип пропадает, а сервер отвечает на фильтр 400. Не
   * сбросив его, мы оставили бы пустой список без единого способа выйти.
   */
  filterAllowed: boolean,
): StudentOccurrencesView {
  /** `null` значит «показываем первую страницу карточки как есть». */
  const [own, setOwn] = useState<Own | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilterState] = useState<PairOccurrenceFilter | null>(null);
  /** Тот же фильтр для эффекта и обработчиков: состояние в замыкании отстаёт. */
  const filterRef = useRef<PairOccurrenceFilter | null>(null);
  /**
   * Поколение списка. Ответ, запрошенный до перечитывания карточки или до
   * смены фильтра, обязан не приклеиться к новому списку: его курсор снят с
   * другого чтения, а строки могут быть из другого фильтра.
   */
  const generation = useRef(0);
  const allowed = useRef(filterAllowed);
  allowed.current = filterAllowed;

  const rows = own?.rows ?? first.rows;
  const cursor = own === null ? first.nextCursor : own.cursor;
  /** Сколько строк на экране: по нему перечитывание решает, сколько просить. */
  const shown = useRef(rows.length);
  shown.current = rows.length;

  /** Первые `depth` строк заново, с текущим фильтром. */
  const reloadHead = (f: PairOccurrenceFilter | null, depth: number): void => {
    generation.current += 1;
    const mine = generation.current;
    setErr(null);
    setBusy(true);
    const limit = Math.min(
      Math.max(depth, STUDENT_LESSON_PAGE_LIMIT_DEFAULT),
      STUDENT_LESSON_PAGE_LIMIT_MAX,
    );
    api
      .getStudentOccurrences(studentId, { limit, filter: allowed.current ? f : null })
      .then((page) => {
        if (generation.current === mine) setOwn({ rows: page.rows, cursor: page.nextCursor });
      })
      .catch((e: unknown) => {
        if (generation.current !== mine) return;
        // Без фильтра честная первая страница из карточки лучше устаревших
        // строк; под фильтром подменять её нечем, и отказ говорится словами
        if (f === null) setOwn(null);
        else setErr(listErrorText(e));
      })
      .finally(() => {
        if (generation.current === mine) setBusy(false);
      });
  };

  /** Показать первую страницу карточки как есть, отменив всё, что в полёте. */
  const showFirst = (): void => {
    generation.current += 1;
    setErr(null);
    setOwn(null);
    setBusy(false);
  };

  /** Фильтр, который уже нельзя задавать, снимается вместе со своими строками. */
  const dropStaleFilter = (): boolean => {
    if (filterRef.current === null || allowed.current) return false;
    filterRef.current = null;
    setFilterState(null);
    showFirst();
    return true;
  };

  /** Первая страница, которую уже видели: пропустить эффект на первом кадре. */
  const seen = useRef(first.rows);

  useEffect(() => {
    if (seen.current === first.rows) return;
    seen.current = first.rows;
    // Карточку перечитали после смены режима оплаты: фильтра больше нет
    if (dropStaleFilter()) return;
    const f = filterRef.current;
    const depth = shown.current;
    if (f === null && depth <= STUDENT_LESSON_PAGE_LIMIT_DEFAULT) {
      showFirst();
      return;
    }
    // Прежние строки остаются на экране до ответа, кнопка при этом занята
    reloadHead(f, depth);
    // `reloadHead` в зависимостях не стоит: он пересоздаётся на каждом кадре, а
    // читает только ref и свои аргументы
  }, [studentId, first.rows]);

  // Учёт оплат выключили в сессии без перечитывания карточки: то же самое
  useEffect(() => {
    dropStaleFilter();
    // `dropStaleFilter` читает только ref, пересоздание его на кадре не важно
  }, [filterAllowed]);

  const setFilter = (f: PairOccurrenceFilter | null): void => {
    if (f === filterRef.current) return;
    if (f !== null && !allowed.current) return;
    filterRef.current = f;
    setFilterState(f);
    if (f === null) {
      showFirst();
      return;
    }
    /*
      Строки без фильтра под нажатым чипом фильтра читались бы как ответ на
      него, поэтому до ответа список пуст и занят, а не прежний.
    */
    setOwn({ rows: [], cursor: null });
    reloadHead(f, STUDENT_LESSON_PAGE_LIMIT_DEFAULT);
  };

  const loadMore = (): void => {
    if (cursor === null || busy) return;
    const mine = generation.current;
    const from = cursor;
    const f = allowed.current ? filterRef.current : null;
    setBusy(true);
    api
      .getStudentOccurrences(studentId, { cursor: from, filter: f })
      .then((page) => {
        if (generation.current !== mine) return;
        setOwn((prev) => {
          const base: Own = prev ?? { rows: [...first.rows], cursor: first.nextCursor };
          // Страница приклеивается только к тому хвосту, с которого её просили
          if (base.cursor !== from) return prev;
          return { rows: [...base.rows, ...page.rows], cursor: page.nextCursor };
        });
        setErr(null);
      })
      .catch((e: unknown) => {
        if (generation.current === mine) setErr(listErrorText(e));
      })
      .finally(() => {
        if (generation.current === mine) setBusy(false);
      });
  };

  return { rows, nextCursor: cursor, busy, err, loadMore, filter, setFilter };
}
